import { createSlice } from '@reduxjs/toolkit';

import type { ProtocoloState } from '@/types/peticionamento/protocoloStoreTypes';

import {
  adicionarDocumento,
  adicionarParte,
  atualizarAceiteTermos,
  atualizarDadosProcesso,
  atualizarDadosTribunal,
  atualizarParte,
  atualizarTipoAssinatura,
  limparPeticionamento,
  removerDocumento,
  removerParte,
  setDadosPeticionamento,
  setDocumentos,
  setEtapaAtual,
  setPartes,
  setPeticionamentoErro,
} from 'src/features/peticionamento/store/actions';
import {
  buscarHistorico,
  buscarStatusProcesso,
  criarPartesBatch,
  finalizarPeticionamento,
} from 'src/features/peticionamento/store/thunks';

const initialState: ProtocoloState = {
  dadosProtocolo: {
    dadosProcesso: {
      tribunal: '',
      numeroProcesso: '',
      classeJudicial: '',
      assunto: '',
      segredoJustica: false,
      prioridade: 'normal',
    },
    partes: [],
    documentos: [],
    aceiteTermos: false,
    tipoAssinatura: 'digital',
    dadosTribunal: {},
  },
  statusProtocolo: { status: 'rascunho' },
  etapaAtual: 0,
  isLoading: false,
  errors: {},
  historico: [],
  statusExecucao: null,
};

const peticionamentoSlice = createSlice({
  name: 'peticionamento',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setPeticionamentoErro, (state, action) => {
        state.errors.geral = action.payload || '';
      })
      .addCase(setEtapaAtual, (state, action) => {
        state.etapaAtual = action.payload;
      })
      .addCase(atualizarDadosProcesso, (state, action) => {
        state.dadosProtocolo.dadosProcesso = {
          ...state.dadosProtocolo.dadosProcesso,
          ...action.payload,
        } as typeof state.dadosProtocolo.dadosProcesso;
      })
      .addCase(setDadosPeticionamento, (state, action) => {
        if (action.payload.dadosProcesso) {
          state.dadosProtocolo.dadosProcesso = {
            ...state.dadosProtocolo.dadosProcesso,
            ...action.payload.dadosProcesso,
          } as typeof state.dadosProtocolo.dadosProcesso;
        }
        if (action.payload.partes !== undefined) {
          state.dadosProtocolo.partes = action.payload.partes;
        }
        if (action.payload.documentos !== undefined) {
          state.dadosProtocolo.documentos = action.payload.documentos;
        }
        if (typeof action.payload.aceiteTermos === 'boolean') {
          state.dadosProtocolo.aceiteTermos = action.payload.aceiteTermos;
        }
        if (action.payload.tipoAssinatura) {
          state.dadosProtocolo.tipoAssinatura = action.payload.tipoAssinatura;
        }
        if (action.payload.observacoesFinais !== undefined) {
          state.dadosProtocolo.observacoesFinais = action.payload.observacoesFinais;
        }
      })
      .addCase(adicionarParte, (state, action) => {
        state.dadosProtocolo.partes.push(action.payload);
      })
      .addCase(setPartes, (state, action) => {
        state.dadosProtocolo.partes = action.payload;
      })
      .addCase(atualizarParte, (state, action) => {
        const { id, dados } = action.payload;
        state.dadosProtocolo.partes = state.dadosProtocolo.partes.map((p) =>
          p.id === id ? { ...p, ...dados } : p,
        );
      })
      .addCase(removerParte, (state, action) => {
        state.dadosProtocolo.partes = state.dadosProtocolo.partes.filter(
          (p) => p.id !== action.payload.id,
        );
      })
      .addCase(adicionarDocumento, (state, action) => {
        state.dadosProtocolo.documentos.push(action.payload);
      })
      .addCase(setDocumentos, (state, action) => {
        state.dadosProtocolo.documentos = action.payload.map((d) => ({
          id: d.id,
          nome: d.nome,
          tipo: d.tipo,
          obrigatorio: d.obrigatorio,
          nivelSigilo: d.nivelSigilo,
          descricao: d.descricao,
        }));
      })
      .addCase(removerDocumento, (state, action) => {
        state.dadosProtocolo.documentos = state.dadosProtocolo.documentos.filter(
          (d) => d.id !== action.payload.id,
        );
      })
      .addCase(atualizarAceiteTermos, (state, action) => {
        state.dadosProtocolo.aceiteTermos = action.payload;
      })
      .addCase(atualizarTipoAssinatura, (state, action) => {
        state.dadosProtocolo.tipoAssinatura = action.payload;
      })
      .addCase(atualizarDadosTribunal, (state, action) => {
        state.dadosProtocolo.dadosTribunal = action.payload;
      })
      .addCase(limparPeticionamento, () => initialState)
      .addCase(finalizarPeticionamento.pending, (state) => {
        state.isLoading = true;
        state.statusProtocolo = { status: 'processando' };
      })
      .addCase(finalizarPeticionamento.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statusProtocolo = {
          status: 'protocolado',
          numero: action.payload.numeroProtocolo || action.payload.numeroSolicitacao,
          dataProtocolo: action.payload.dataProtocolo,
          mensagem: action.payload.mensagem,
        };
      })
      .addCase(finalizarPeticionamento.rejected, (state) => {
        state.isLoading = false;
        state.statusProtocolo = {
          status: 'erro',
          mensagem: 'Erro ao finalizar peticionamento. Tente novamente.',
        };
      })
      .addCase(criarPartesBatch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(criarPartesBatch.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(criarPartesBatch.rejected, (state) => {
        state.isLoading = false;
        state.errors.geral = 'Erro ao criar partes';
      })
      .addCase(buscarHistorico.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(buscarHistorico.fulfilled, (state, action) => {
        state.isLoading = false;
        state.historico = action.payload;
      })
      .addCase(buscarHistorico.rejected, (state) => {
        state.isLoading = false;
        state.errors.geral = 'Erro ao buscar histórico';
      })
      .addCase(buscarStatusProcesso.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(buscarStatusProcesso.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statusExecucao = action.payload;
      })
      .addCase(buscarStatusProcesso.rejected, (state) => {
        state.isLoading = false;
        state.errors.geral = 'Erro ao buscar status';
      });
  },
});

export const peticionamentoReducer = peticionamentoSlice.reducer;
